import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { AutoComplete } from 'primereact/autocomplete';
import { SelectButton } from 'primereact/selectbutton';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import OrgChartTree from '../../components/tree/OrgChartTree';
import OrgChartPositionPanel from '../../components/tree/OrgChartPositionPanel';
import OrgChartMLMModal from '../../components/tree/OrgChartMLMModal';
import { orgChartService } from '../../services/orgChartService';
import axiosInstance from '../../config/axios.config';

/**
 * OrgChartPage Component
 * Professional organizational chart management with inline actions
 *
 * Features:
 * - Interactive org chart tree with context menus
 * - Inline add child/sibling from nodes
 * - Right-click and three-dot menus on nodes
 * - User assignment with visual indicators
 * - Role-based access control
 *
 * Access Control:
 * - View: All company employees
 * - Manage: tenant_admin, company_admin, company_owner only
 */
const OrgChartPage = () => {
    const [treeData, setTreeData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [showAssignUserDialog, setShowAssignUserDialog] = useState(false);
    const [selectedPosition, setSelectedPosition] = useState(null);
    const [positions, setPositions] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    // Form states
    const [positionName, setPositionName] = useState('');
    const [parentPositionId, setParentPositionId] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);

    // User search states
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [searchTimeout, setSearchTimeout] = useState(null);

    // Side Panel State for MLM Performance
    const [selectedPositionId, setSelectedPositionId] = useState(null);
    const [positionData, setPositionData] = useState(null);
    const [positionLoading, setPositionLoading] = useState(false);
    const [positionError, setPositionError] = useState(null);
    const [currentFilter, setCurrentFilter] = useState('whole_branch');

    // MLM Modal State
    const [showMLMModal, setShowMLMModal] = useState(false);
    const [mlmModalPositionData, setMlmModalPositionData] = useState(null);

    // Search State
    const [showSearchDialog, setShowSearchDialog] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchType, setSearchType] = useState('position');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchDebounceTimeout, setSearchDebounceTimeout] = useState(null);

    // Path navigation state (for search result navigation)
    const [highlightedPath, setHighlightedPath] = useState([]);
    const [selectedNavigationPositionId, setSelectedNavigationPositionId] = useState(null);

    // Lazy Loading State (like MLM)
    const [expandingPositionId, setExpandingPositionId] = useState(null);

    // Ref to store expand function from visualization component
    const expandNodeRef = useRef(null);

    const toast = useRef(null);

    // Get user roles for access control
    const userRoles = JSON.parse(localStorage.getItem('userRoles') || '[]');
    const userInfo = JSON.parse(localStorage.getItem('user') || '{}');
    const primaryRole = userRoles.length > 0 ? userRoles[0] : (userInfo.user_type || 'user');
    const isAdmin = primaryRole === 'tenant_admin' || primaryRole === 'owner' ||
                    primaryRole === 'company_admin' || primaryRole === 'company_owner';

    useEffect(() => {
        fetchOrgChart();
        if (isAdmin) {
            fetchPositionsList();
            // Don't fetch users on mount - wait for user to search
        }
        // Fetch authenticated user's own position MLM performance by default
        fetchPositionMLMPerformance(null, 'whole_branch');
    }, []);

    // Cleanup timeouts on unmount
    useEffect(() => {
        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
            if (searchDebounceTimeout) {
                clearTimeout(searchDebounceTimeout);
            }
        };
    }, [searchTimeout, searchDebounceTimeout]);

    const fetchOrgChart = async () => {
        setLoading(true);
        try {
            // CHANGED: Use shallow tree endpoint for lazy loading (only roots + direct children)
            const response = await axiosInstance.get('/tree/org-chart/tree-shallow');
            if (response.data.success) {
                setTreeData(response.data.data);
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: response.data.message || 'Failed to load org chart',
                    life: 5000
                });
            }
        } catch (error) {
            console.error('Error fetching org chart:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to load organizational chart',
                life: 5000
            });
        } finally {
            setLoading(false);
        }
    };

    // Add newly created position to tree without reloading
    const addPositionToTree = (newPosition) => {
        setTreeData(prevTree => {
            if (!prevTree || prevTree.length === 0) {
                return prevTree;
            }

            // Deep clone the tree
            const updatedTree = JSON.parse(JSON.stringify(prevTree));

            // Find parent position in tree
            const findAndAddToParent = (nodes) => {
                for (let node of nodes) {
                    if (node.id === newPosition.parent_position_id) {

                        // Found parent - add new position to children
                        if (!node.children) {
                            node.children = [];
                        }

                        // Add new position with required structure
                        const newNode = {
                            id: newPosition.id,
                            position_name: newPosition.position_name,
                            position_code: newPosition.position_code || null,
                            level: newPosition.level,
                            parent_position_id: newPosition.parent_position_id,
                            has_children: false, // New position has no children
                            children: [], // Empty children array
                            active_users: [] // No users assigned yet
                        };

                        node.children.push(newNode);
                        return true;
                    }

                    // Recursively search children
                    if (node.children && node.children.length > 0) {
                        if (findAndAddToParent(node.children)) {
                            return true;
                        }
                    }
                }
                return false;
            };

            const found = findAndAddToParent(updatedTree);

            if (!found) {
                console.log('WARNING: Parent position not found in tree. Parent may not be loaded yet.');

                // Show user-friendly message
                toast.current?.show({
                    severity: 'info',
                    summary: 'Position Created',
                    detail: 'Position created successfully. Expand the parent position to see it, or refresh the tree.',
                    life: 5000
                });
            }

            return updatedTree;
        });
    };

    // Update existing position in tree
    const updatePositionInTree = (updatedPosition) => {
        setTreeData(prevTree => {
            if (!prevTree || prevTree.length === 0) return prevTree;

            // Deep clone the tree
            const updatedTree = JSON.parse(JSON.stringify(prevTree));

            // Find and update position
            const findAndUpdate = (nodes) => {
                for (let node of nodes) {
                    if (node.id === updatedPosition.id) {
                        // Update position properties
                        node.position_name = updatedPosition.position_name;
                        node.position_code = updatedPosition.position_code || null;
                        return true;
                    }

                    // Recursively search children
                    if (node.children && node.children.length > 0) {
                        if (findAndUpdate(node.children)) {
                            return true;
                        }
                    }
                }
                return false;
            };

            findAndUpdate(updatedTree);
            return updatedTree;
        });
    };

    // Remove position from tree
    const removePositionFromTree = (positionId) => {
        setTreeData(prevTree => {
            if (!prevTree || prevTree.length === 0) return prevTree;

            // Deep clone the tree
            const updatedTree = JSON.parse(JSON.stringify(prevTree));

            // Find and remove position
            const findAndRemove = (nodes) => {
                for (let i = 0; i < nodes.length; i++) {
                    if (nodes[i].id === positionId) {
                        // Remove this position
                        nodes.splice(i, 1);
                        return true;
                    }

                    // Recursively search children
                    if (nodes[i].children && nodes[i].children.length > 0) {
                        if (findAndRemove(nodes[i].children)) {
                            return true;
                        }
                    }
                }
                return false;
            };

            findAndRemove(updatedTree);
            return updatedTree;
        });
    };

    // Assign user to position in tree
    const assignUserToPosition = (positionId, userData) => {
        setTreeData(prevTree => {
            if (!prevTree || prevTree.length === 0) return prevTree;

            // Deep clone the tree
            const updatedTree = JSON.parse(JSON.stringify(prevTree));

            // STEP 1: First, remove user from any existing position
            // (Backend does this automatically, so UI must match)
            const removeUserFromAllPositions = (nodes) => {
                for (let node of nodes) {
                    if (node.active_users && Array.isArray(node.active_users)) {
                        // Remove this user from active_users if present
                        const originalLength = node.active_users.length;
                        node.active_users = node.active_users.filter(u => u.id !== userData.id);

                        // Log if we removed the user from a position
                        if (originalLength !== node.active_users.length) {
                            console.log(`Removed user ${userData.name} from position: ${node.position_name}`);
                        }
                    }

                    // Recursively search children
                    if (node.children && node.children.length > 0) {
                        removeUserFromAllPositions(node.children);
                    }
                }
            };

            removeUserFromAllPositions(updatedTree);

            // STEP 2: Now assign user to the new position
            const findAndAssign = (nodes) => {
                for (let node of nodes) {
                    if (node.id === positionId) {
                        // Initialize active_users if not exists
                        if (!node.active_users) {
                            node.active_users = [];
                        }

                        // Add user to active_users array
                        node.active_users.push({
                            id: userData.id,
                            name: userData.name,
                            email: userData.email,
                            mobile_number: userData.mobile_number || null
                        });

                        console.log(`Assigned user ${userData.name} to position: ${node.position_name}`);
                        return true;
                    }

                    // Recursively search children
                    if (node.children && node.children.length > 0) {
                        if (findAndAssign(node.children)) {
                            return true;
                        }
                    }
                }
                return false;
            };

            findAndAssign(updatedTree);
            return updatedTree;
        });
    };

    // Remove user from position in tree
    const removeUserFromPosition = (positionId, userId) => {
        setTreeData(prevTree => {
            if (!prevTree || prevTree.length === 0) return prevTree;

            // Deep clone the tree
            const updatedTree = JSON.parse(JSON.stringify(prevTree));

            // Find and remove user from position
            const findAndRemoveUser = (nodes) => {
                for (let node of nodes) {
                    if (node.id === positionId) {
                        // Remove user from active_users array
                        if (node.active_users && Array.isArray(node.active_users)) {
                            node.active_users = node.active_users.filter(u => u.id !== userId);
                        }
                        return true;
                    }

                    // Recursively search children
                    if (node.children && node.children.length > 0) {
                        if (findAndRemoveUser(node.children)) {
                            return true;
                        }
                    }
                }
                return false;
            };

            findAndRemoveUser(updatedTree);
            return updatedTree;
        });
    };

    const fetchPositionsList = async () => {
        try {
            const response = await axiosInstance.get('/tree/org-chart');
            if (response.data.success) {
                setPositions(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching positions list:', error);
        }
    };

    const searchUsers = useCallback(async (searchTerm) => {
        // Don't search if less than 3 characters
        if (searchTerm.length < 3) {
            setFilteredUsers([]);
            return;
        }

        setLoadingUsers(true);
        try {
            const url = `/users?search=${encodeURIComponent(searchTerm)}&limit=20`;
            const response = await axiosInstance.get(url);

            if (response.data.success) {
                const usersList = response.data.items || response.data.data || response.data.users || [];
                setFilteredUsers(usersList);
            }
        } catch (error) {
            console.error('Error fetching users list:', error);
            setFilteredUsers([]);
        } finally {
            setLoadingUsers(false);
        }
    }, []);

    // Handle user search with debouncing for AutoComplete
    const handleCompleteSearch = (event) => {
        const query = event.query || '';
        setUserSearchTerm(query);

        // Clear existing timeout
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        // Set new timeout for debouncing (500ms delay)
        const timeout = setTimeout(() => {
            searchUsers(query);
        }, 500);

        setSearchTimeout(timeout);
    };

    // Handle inline "Add Child" from node
    const handleAddChild = (parentNode) => {
        setSelectedPosition(parentNode);
        setParentPositionId(parentNode.id);
        setPositionName('');
        setShowCreateDialog(true);
    };

    // Handle inline "Add Sibling" from node
    const handleAddSibling = (siblingNode) => {
        setSelectedPosition(null);
        setParentPositionId(siblingNode.parent_position_id);
        setPositionName('');
        setShowCreateDialog(true);
    };

    const handleCreatePosition = async () => {
        if (!positionName.trim()) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Validation Error',
                detail: 'Position name is required',
                life: 3000
            });
            return;
        }

        setSubmitting(true);
        try {
            const response = await axiosInstance.post('/tree/org-chart', {
                position_name: positionName,
                parent_position_id: parentPositionId
            });

            if (response.data.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Position created successfully',
                    life: 3000
                });

                setShowCreateDialog(false);
                resetForm();

                // Add new position to tree without reloading
                const newPosition = response.data.data;
                addPositionToTree(newPosition);
            }
        } catch (error) {
            console.error('Error creating position:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to create position',
                life: 5000
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Handle inline "Edit Position" from node
    const handleEditPosition = (position) => {
        setSelectedPosition(position);
        setPositionName(position.position_name);
        setParentPositionId(position.parent_position_id);
        setShowEditDialog(true);
    };

    const handleUpdatePosition = async () => {
        if (!positionName.trim()) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Validation Error',
                detail: 'Position name is required',
                life: 3000
            });
            return;
        }

        setSubmitting(true);
        try {
            const response = await axiosInstance.put(`/tree/org-chart/${selectedPosition.id}`, {
                position_name: positionName,
                parent_position_id: parentPositionId
            });

            if (response.data.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Position updated successfully',
                    life: 3000
                });

                setShowEditDialog(false);
                resetForm();

                // Update position in tree without reloading
                const updatedPosition = response.data.data;
                updatePositionInTree(updatedPosition);
            }
        } catch (error) {
            console.error('Error updating position:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to update position',
                life: 5000
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Handle inline "Delete Position" from node
    const handleDeletePosition = (position) => {
        confirmDialog({
            message: `Are you sure you want to delete the position "${position.position_name}"?`,
            header: 'Confirm Deletion',
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                try {
                    const response = await axiosInstance.delete(`/tree/org-chart/${position.id}`);

                    if (response.data.success) {
                        toast.current?.show({
                            severity: 'success',
                            summary: 'Success',
                            detail: 'Position deleted successfully',
                            life: 3000
                        });

                        // Remove position from tree without reloading
                        removePositionFromTree(position.id);
                    }
                } catch (error) {
                    console.error('Error deleting position:', error);
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.response?.data?.message || 'Failed to delete position',
                        life: 5000
                    });
                }
            }
        });
    };

    // Handle inline "Assign User" from node
    const handleAssignUser = (position) => {
        setSelectedPosition(position);
        setSelectedUser(null);
        setUserSearchTerm('');
        setFilteredUsers([]);
        setShowAssignUserDialog(true);
    };

    const handleAssignUserSubmit = async () => {
        if (!selectedUser || !selectedUser.id) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Validation Error',
                detail: 'Please select a user',
                life: 3000
            });
            return;
        }

        setSubmitting(true);
        try {
            const response = await axiosInstance.post(`/tree/org-chart/${selectedPosition.id}/assign-user`, {
                user_id: selectedUser.id
            });

            if (response.data.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'User assigned successfully',
                    life: 3000
                });

                setShowAssignUserDialog(false);
                resetForm();

                // Update tree to show assigned user
                assignUserToPosition(selectedPosition.id, selectedUser);
            }
        } catch (error) {
            console.error('Error assigning user:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to assign user',
                life: 5000
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Handle inline "Remove User" from node
    const handleRemoveUser = (position) => {
        if (!position.active_users || position.active_users.length === 0) {
            return;
        }

        const userName = position.active_users[0]?.name || 'this user';

        confirmDialog({
            message: `Remove ${userName} from position "${position.position_name}"?`,
            header: 'Confirm User Removal',
            icon: 'pi pi-exclamation-triangle',
            accept: async () => {
                try {
                    const userId = position.active_users[0].id;
                    const response = await axiosInstance.post(`/tree/org-chart/${position.id}/remove-user`, {
                        user_id: userId
                    });

                    if (response.data.success) {
                        toast.current?.show({
                            severity: 'success',
                            summary: 'Success',
                            detail: 'User removed successfully',
                            life: 3000
                        });

                        // Update tree to remove user from position
                        removeUserFromPosition(position.id, userId);
                    }
                } catch (error) {
                    console.error('Error removing user:', error);
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.response?.data?.message || 'Failed to remove user',
                        life: 5000
                    });
                }
            }
        });
    };

    /**
     * Fetch position MLM performance data from API
     * @param {number|null} positionId - Position ID (null = auth user's position)
     * @param {string} filter - Filter type: 'whole_branch' or 'this_position_only'
     */
    const fetchPositionMLMPerformance = async (positionId = null, filter = 'whole_branch') => {
        console.log('OrgChartPage: fetchPositionMLMPerformance called with positionId:', positionId, 'filter:', filter);
        setPositionLoading(true);
        setPositionError(null);
        setSelectedPositionId(positionId);

        try {
            console.log('OrgChartPage: Calling orgChartService.getPositionMLMPerformance');
            const response = await orgChartService.getPositionMLMPerformance(positionId, filter);
            console.log('OrgChartPage: API response:', response);

            if (response.success) {
                setPositionData(response.data);
            } else {
                setPositionError(response.message || 'Failed to load position MLM performance data');
            }
        } catch (error) {
            console.error('OrgChartPage: Error fetching position MLM performance:', error);
            setPositionError(error.message || 'Failed to load position MLM performance data');
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to load position MLM performance data',
                life: 3000
            });
        } finally {
            setPositionLoading(false);
        }
    };

    /**
     * Handle position click from tree - fetch MLM performance
     */
    const handlePositionClick = (position) => {
        console.log('OrgChartPage: handlePositionClick called with position:', position);
        setSelectedPosition(position);
        if (position && position.id) {
            fetchPositionMLMPerformance(position.id, currentFilter);
        } else {
            console.warn('OrgChartPage: handlePositionClick called with invalid position');
        }
    };

    /**
     * Handle filter change from side panel
     */
    const handleFilterChange = (newFilter) => {
        console.log('OrgChartPage: handleFilterChange called with filter:', newFilter);
        // PrimeReact SelectButton sends null when clicking already-selected option (deselect)
        if (!newFilter) return;
        setCurrentFilter(newFilter);
        if (selectedPositionId) {
            fetchPositionMLMPerformance(selectedPositionId, newFilter);
        }
    };

    /**
     * Handle "View MLM Tree" button click from position panel
     */
    const handleViewMLMTree = (positionData) => {
        console.log('OrgChartPage: handleViewMLMTree called with positionData:', positionData);
        setMlmModalPositionData(positionData);
        setShowMLMModal(true);
    };

    /**
     * Handle MLM modal close
     */
    const handleMLMModalHide = () => {
        setShowMLMModal(false);
        setMlmModalPositionData(null);
        // Note: We don't re-fetch org chart here as per requirement
    };

    /**
     * Handle search query change with debouncing
     */
    const handleSearchQueryChange = (e) => {
        const query = e.target.value;
        setSearchQuery(query);

        // Clear existing timeout
        if (searchDebounceTimeout) {
            clearTimeout(searchDebounceTimeout);
        }

        // Don't search if query is less than 2 characters
        if (query.length < 2) {
            setSearchResults([]);
            return;
        }

        // Set new timeout for debouncing (300ms delay like MLM)
        const timeout = setTimeout(() => {
            performSearch(query, searchType);
        }, 300);

        setSearchDebounceTimeout(timeout);
    };

    /**
     * Perform search API call
     */
    const performSearch = async (query, type) => {
        if (query.length < 2) {
            setSearchResults([]);
            return;
        }

        setSearchLoading(true);
        try {
            const response = await orgChartService.searchPositions(query, type, 10);

            if (response.success) {
                setSearchResults(response.data.results || []);
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Search Error',
                    detail: response.message || 'Failed to search positions',
                    life: 3000
                });
                setSearchResults([]);
            }
        } catch (error) {
            console.error('Search error:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Search Error',
                detail: error.message || 'Failed to search positions',
                life: 3000
            });
            setSearchResults([]);
        } finally {
            setSearchLoading(false);
        }
    };

    /**
     * Handle search type change
     */
    const handleSearchTypeChange = (e) => {
        const newType = e.value;
        setSearchType(newType);

        // Re-trigger search if there's a query
        if (searchQuery.length >= 2) {
            performSearch(searchQuery, newType);
        }
    };

    /**
     * Handle search result click - navigate to position
     */
    const handleSearchResultClick = async (position) => {
        try {
            // Get path to position
            const pathResponse = await orgChartService.getPositionPath(position.id);

            if (pathResponse.success) {
                const pathData = pathResponse.data;
                const pathIds = pathData.path.map(p => p.id);

                // Set highlighted path and selected position
                setHighlightedPath(pathIds);
                setSelectedNavigationPositionId(position.id);

                // Also load position MLM performance
                handlePositionClick(position);

                toast.current?.show({
                    severity: 'success',
                    summary: 'Navigation',
                    detail: `Navigated to: ${position.position_name}`,
                    life: 2000
                });

                setShowSearchDialog(false);
            }
        } catch (error) {
            console.error('Navigation error:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Navigation Error',
                detail: error.message || 'Failed to navigate to position',
                life: 3000
            });
        }
    };

    /**
     * Handle search dialog close
     */
    const handleSearchDialogHide = () => {
        setShowSearchDialog(false);
        setSearchQuery('');
        setSearchResults([]);
        setSearchType('position');

        // Clear any pending timeout
        if (searchDebounceTimeout) {
            clearTimeout(searchDebounceTimeout);
        }
    };

    const resetForm = () => {
        setPositionName('');
        setParentPositionId(null);
        setSelectedUser(null);
        setSelectedPosition(null);
        setUserSearchTerm('');
        setFilteredUsers([]);
    };

    /**
     * Load children for a position (lazy loading)
     * Matches MLM's loadNodeChildren() pattern
     */
    const loadPositionChildren = useCallback(async (positionId) => {
        console.log('Loading children for position:', positionId);
        setExpandingPositionId(positionId);

        try {
            // Call children endpoint without limit to get ALL direct children
            const response = await axiosInstance.get(`/tree/org-chart/${positionId}/children`);

            if (response.data.success) {
                console.log('Children loaded:', response.data.data.children);
                updateTreeWithChildren(positionId, response.data.data.children);

                // Wait for React to commit state, then expand the node (100ms like MLM)
                setTimeout(() => {
                    if (expandNodeRef.current) {
                        console.log('Expanding position after data load:', positionId);
                        expandNodeRef.current(positionId);
                    } else {
                        console.warn('expandNodeRef.current is null - cannot expand position');
                    }
                }, 100);
            } else {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: response.data.message || 'Failed to load children',
                    life: 3000
                });
            }
        } catch (error) {
            console.error('Error loading children:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Failed to load children',
                life: 3000
            });
        } finally {
            setExpandingPositionId(null);
        }
    }, []);

    /**
     * Update tree data with loaded children
     * Handles multiple root nodes (unlike MLM which has single root)
     */
    const updateTreeWithChildren = useCallback((positionId, children) => {
        console.log('Updating tree with children for position:', positionId, children);

        setTreeData(prevTree => {
            const updatedTree = JSON.parse(JSON.stringify(prevTree)); // Deep clone

            // Recursive function to find and update the node
            const updateNode = (nodes) => {
                for (let i = 0; i < nodes.length; i++) {
                    if (nodes[i].id === positionId) {
                        // Found the node - update its children
                        nodes[i].children = children;
                        console.log('Updated node children:', nodes[i]);
                        return true;
                    }

                    // Recursively search in children
                    if (nodes[i].children && nodes[i].children.length > 0) {
                        if (updateNode(nodes[i].children)) {
                            return true;
                        }
                    }
                }
                return false;
            };

            updateNode(updatedTree);
            return updatedTree;
        });
    }, []);


    if (loading) {
        return (
            <div className="flex align-items-center justify-content-center" style={{ minHeight: '80vh' }}>
                <ProgressSpinner />
            </div>
        );
    }

    return (
        <div className="org-chart-page flex flex-column" style={{ height: 'calc(100vh - 120px)', overflow: 'hidden' }}>
            <Toast ref={toast} />
            <ConfirmDialog />

            {/* Compact Professional Toolbar - Responsive */}
            <div className="surface-card border-round shadow-1 p-2 md:p-3 mb-2 md:mb-3 flex-shrink-0">
                <div className="flex flex-column md:flex-row align-items-start md:align-items-center justify-content-between gap-2">
                    <div className="flex align-items-center gap-2 md:gap-3">
                        <i className="pi pi-sitemap text-xl md:text-2xl text-primary"></i>
                        <h2 className="m-0 text-lg md:text-xl font-semibold text-900">Organizational Chart</h2>
                    </div>

                    <div className="flex align-items-center gap-2 flex-wrap">
                        {!isAdmin && (
                            <span className="text-sm text-600 mr-2 hidden md:inline-flex align-items-center">
                                <i className="pi pi-eye mr-1"></i>
                                View Only
                            </span>
                        )}
                        <Button
                            icon="pi pi-refresh"
                            className="p-button-text p-button-sm"
                            onClick={fetchOrgChart}
                            tooltip="Refresh"
                            tooltipOptions={{ position: 'bottom' }}
                        />
                        {isAdmin && (
                            <Button
                                label={window.innerWidth < 768 ? '' : 'Create Root Position'}
                                icon="pi pi-plus"
                                onClick={() => {
                                    setParentPositionId(null);
                                    setShowCreateDialog(true);
                                }}
                                className="p-button-sm"
                                tooltip={window.innerWidth < 768 ? 'Create Root Position' : undefined}
                                tooltipOptions={{ position: 'bottom' }}
                            />
                        )}
                    </div>
                </div>
            </div>

            {/* Main Content Container - Responsive: column on mobile, row on desktop */}
            <div className="flex flex-column lg:flex-row gap-2 md:gap-3 flex-1 overflow-hidden org-chart-content">
                {/* Left: Org Chart Tree - Full width on mobile, 60% on desktop */}
                <div className="surface-card border-round shadow-1 overflow-hidden org-chart-tree-container" style={{ minHeight: '300px' }}>
                    <OrgChartTree
                        treeData={treeData}
                        onPositionClick={handlePositionClick}
                        onAddChild={handleAddChild}
                        onAddSibling={handleAddSibling}
                        onEditPosition={handleEditPosition}
                        onDeletePosition={handleDeletePosition}
                        onAssignUser={handleAssignUser}
                        onRemoveUser={handleRemoveUser}
                        loading={loading}
                        isAdmin={isAdmin}
                        highlightedPath={highlightedPath}
                        selectedPositionId={selectedNavigationPositionId}
                        onSearchResultClick={handleSearchResultClick}
                        toast={toast}
                        onLoadChildren={loadPositionChildren}
                        expandingPositionId={expandingPositionId}
                        expandNodeRef={expandNodeRef}
                    />
                </div>

                {/* Right: Position MLM Performance Panel - Full width on mobile, 40% on desktop */}
                <div className="surface-card border-round shadow-1 overflow-auto p-3 org-chart-panel-container">
                    <OrgChartPositionPanel
                        positionData={positionData}
                        loading={positionLoading}
                        error={positionError}
                        onFilterChange={handleFilterChange}
                        currentFilter={currentFilter}
                        onViewMLMTree={handleViewMLMTree}
                    />
                </div>
            </div>

            {/* Responsive CSS for OrgChart layout */}
            <style>{`
                /* Mobile First - Stack vertically */
                .org-chart-content {
                    flex-direction: column;
                }
                .org-chart-tree-container {
                    flex: 1 1 auto;
                    min-height: 50vh;
                    height: auto;
                }
                .org-chart-panel-container {
                    flex: 0 0 auto;
                    max-height: 40vh;
                }

                /* Desktop - Side by side */
                @media (min-width: 992px) {
                    .org-chart-content {
                        flex-direction: row;
                    }
                    .org-chart-tree-container {
                        flex: 0 0 60%;
                        min-height: auto;
                        height: 100%;
                    }
                    .org-chart-panel-container {
                        flex: 0 0 40%;
                        max-height: none;
                        height: 100%;
                    }
                }
            `}</style>

                {/* Create Position Dialog */}
                <Dialog
                    header={parentPositionId ? "Create Child Position" : "Create Root Position"}
                    visible={showCreateDialog}
                    style={{ width: '450px' }}
                    breakpoints={{ '768px': '90vw', '576px': '95vw' }}
                    onHide={() => {
                        setShowCreateDialog(false);
                        resetForm();
                    }}
                    footer={
                        <div>
                            <Button
                                label="Cancel"
                                icon="pi pi-times"
                                onClick={() => {
                                    setShowCreateDialog(false);
                                    resetForm();
                                }}
                                className="p-button-text"
                            />
                            <Button
                                label="Create"
                                icon="pi pi-check"
                                onClick={handleCreatePosition}
                                loading={submitting}
                            />
                        </div>
                    }
                >
                    {parentPositionId && (
                        <Message
                            severity="info"
                            text={`Creating child position under: ${selectedPosition?.position_name || positions.find(p => p.id === parentPositionId)?.position_name}`}
                            className="mb-3"
                        />
                    )}

                    <div className="field">
                        <label htmlFor="position-name" className="font-semibold">Position Name *</label>
                        <InputText
                            id="position-name"
                            value={positionName}
                            onChange={(e) => setPositionName(e.target.value)}
                            className="w-full mt-2"
                            placeholder="e.g., Chief Technology Officer"
                            autoFocus
                        />
                    </div>

                    {!parentPositionId && (
                        <div className="field mt-4">
                            <label htmlFor="parent-position" className="font-semibold">Parent Position (Optional)</label>
                            <Dropdown
                                id="parent-position"
                                value={parentPositionId}
                                options={positions}
                                onChange={(e) => setParentPositionId(e.value)}
                                optionLabel="position_name"
                                optionValue="id"
                                placeholder="Select parent position"
                                className="w-full mt-2"
                                showClear
                            />
                        </div>
                    )}
                </Dialog>

                {/* Edit Position Dialog */}
                <Dialog
                    header="Edit Position"
                    visible={showEditDialog}
                    style={{ width: '450px' }}
                    breakpoints={{ '768px': '90vw', '576px': '95vw' }}
                    onHide={() => {
                        setShowEditDialog(false);
                        resetForm();
                    }}
                    footer={
                        <div>
                            <Button
                                label="Cancel"
                                icon="pi pi-times"
                                onClick={() => {
                                    setShowEditDialog(false);
                                    resetForm();
                                }}
                                className="p-button-text"
                            />
                            <Button
                                label="Update"
                                icon="pi pi-check"
                                onClick={handleUpdatePosition}
                                loading={submitting}
                            />
                        </div>
                    }
                >
                    <div className="field">
                        <label htmlFor="edit-position-name" className="font-semibold">Position Name *</label>
                        <InputText
                            id="edit-position-name"
                            value={positionName}
                            onChange={(e) => setPositionName(e.target.value)}
                            className="w-full mt-2"
                            placeholder="Enter position name"
                        />
                    </div>

                    <div className="field mt-4">
                        <label htmlFor="edit-parent-position" className="font-semibold">Parent Position (Optional)</label>
                        <Dropdown
                            id="edit-parent-position"
                            value={parentPositionId}
                            options={positions.filter(p => p.id !== selectedPosition?.id)}
                            onChange={(e) => setParentPositionId(e.value)}
                            optionLabel="position_name"
                            optionValue="id"
                            placeholder="Select parent position"
                            className="w-full mt-2"
                            showClear
                        />
                    </div>
                </Dialog>

                {/* Assign User Dialog */}
                <Dialog
                    header="Assign User to Position"
                    visible={showAssignUserDialog}
                    style={{ width: '450px' }}
                    breakpoints={{ '768px': '90vw', '576px': '95vw' }}
                    onHide={() => {
                        setShowAssignUserDialog(false);
                        resetForm();
                    }}
                    footer={
                        <div>
                            <Button
                                label="Cancel"
                                icon="pi pi-times"
                                onClick={() => {
                                    setShowAssignUserDialog(false);
                                    resetForm();
                                }}
                                className="p-button-text"
                            />
                            <Button
                                label="Assign"
                                icon="pi pi-check"
                                onClick={handleAssignUserSubmit}
                                loading={submitting}
                            />
                        </div>
                    }
                >
                    <div className="field">
                        <label className="font-semibold">Position: {selectedPosition?.position_name}</label>
                    </div>

                    <div className="field mt-4">
                        <label htmlFor="user-select" className="font-semibold">Select User *</label>
                        <AutoComplete
                            id="user-select"
                            value={selectedUser}
                            suggestions={filteredUsers}
                            completeMethod={handleCompleteSearch}
                            onChange={(e) => setSelectedUser(e.value)}
                            field="name"
                            placeholder="Type at least 3 characters to search..."
                            className="w-full mt-2"
                            inputClassName="w-full"
                            minLength={3}
                            disabled={loadingUsers}
                            itemTemplate={(item) => (
                                <div>
                                    <div style={{ fontWeight: 500 }}>{item.name}</div>
                                    <div style={{ fontSize: '0.875rem', color: '#64748b' }}>{item.email}</div>
                                </div>
                            )}
                            emptyMessage={
                                userSearchTerm.length < 3
                                    ? "Type at least 3 characters to search"
                                    : "No users found"
                            }
                        />
                        {loadingUsers && (
                            <small className="text-500 mt-1 block">
                                <i className="pi pi-spin pi-spinner" style={{ fontSize: '0.8rem', marginRight: '4px' }}></i>
                                Searching users...
                            </small>
                        )}
                    </div>
                </Dialog>

            {/* MLM Deep Dive Modal */}
            <OrgChartMLMModal
                visible={showMLMModal}
                onHide={handleMLMModalHide}
                positionData={mlmModalPositionData}
            />

        </div>
    );
};

export default OrgChartPage;
